/**
 * Hook React Query para adjuntos por artefacto del Action Center (0674 F3 piloto multi-modelo).
 *
 * Endpoints backend (ADR-029 implementacion · BACKLOG [0666b][DETAIL-ATTACHMENTS]):
 *   GET    /api/v1/artefacto-adjuntos?artefactoTipo=...&artefactoId=...
 *   POST   /api/v1/artefacto-adjuntos                                   (multipart · campo 'file' + body)
 *   GET    /api/v1/artefacto-adjuntos/:id/descarga                      (signed URL 5min)
 *   DELETE /api/v1/artefacto-adjuntos/:id                               (soft-delete)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BASE, getToken, request } from '../../../shared/services/api';
import type { ArtefactoWorkflowTipo } from './useActionCenterDetail';

export interface ArtefactoAdjunto {
  id: string;
  tenantId: string;
  artefactoTipo: ArtefactoWorkflowTipo;
  artefactoId: string;
  nombreArchivo: string;
  mimeType: string;
  tamanoBytes: number;
  storagePath: string;
  subidoPor: string;
  creadoEn: string;
  eliminadoEn: string | null;
}

export interface DescargaResponse {
  url: string;
  nombreArchivo: string;
  mimeType: string;
  expiresInSeconds: number;
}

const QUERY_KEY = (tipo: ArtefactoWorkflowTipo, id: string) =>
  ['artefactoAdjuntos', tipo, id] as const;

export function useArtefactoAdjuntos(
  artefactoTipo: ArtefactoWorkflowTipo | null,
  artefactoId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEY(artefactoTipo!, artefactoId!),
    queryFn: async (): Promise<ArtefactoAdjunto[]> => {
      const params = new URLSearchParams({
        artefactoTipo: artefactoTipo!,
        artefactoId: artefactoId!,
      });
      const res = await request<{ data: ArtefactoAdjunto[] }>(
        `/artefacto-adjuntos?${params.toString()}`,
      );
      return res.data;
    },
    enabled: enabled && !!artefactoTipo && !!artefactoId,
    staleTime: 30_000,
  });
}

export function useSubirAdjunto(artefactoTipo: ArtefactoWorkflowTipo, artefactoId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<ArtefactoAdjunto> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('artefactoTipo', artefactoTipo);
      formData.append('artefactoId', artefactoId);
      // HIGH-1 fix Codex review 0674: NO usar request() porque fuerza
      // Content-Type: application/json y rompe boundary multipart (multer no lo ve).
      // Patron canonico = fetch manual + Bearer manual (ver subirArchivo en api.ts).
      const token = getToken();
      const res = await fetch(`${BASE}/artefacto-adjuntos`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? `Error ${res.status}`);
      }
      const json = (await res.json()) as { data: ArtefactoAdjunto };
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(artefactoTipo, artefactoId) });
    },
  });
}

export function useEliminarAdjunto(artefactoTipo: ArtefactoWorkflowTipo, artefactoId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (adjuntoId: string): Promise<void> => {
      await request(`/artefacto-adjuntos/${adjuntoId}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(artefactoTipo, artefactoId) });
    },
  });
}

export async function descargarAdjunto(adjuntoId: string): Promise<DescargaResponse> {
  const res = await request<{ data: DescargaResponse }>(
    `/artefacto-adjuntos/${adjuntoId}/descarga`,
  );
  return res.data;
}

export function formatTamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
}
